import { ConfigConstants } from '../../config/config-constants';
import {
    InputFieldPropertyModel,
    ODataInputFieldPropertyModel,
} from '../../models/low-level/input-field-property.model';
import { InputFieldPropertyState } from './input-field-properties-collection.state';

/**
 * Assemble a store entry straight from the OData wire format. This owns all of the coercion the
 * backend requires — numeric fields arrive as strings, booleans as either `'X'`/`''` or real
 * booleans, and an absent `Process` defaults to INVIVO. The OData shape is plain data, so the
 * store layer can read it without pulling in a legacy model.
 *
 * @param data one entry of the OData InputFieldProperty set
 * @returns the serializable store shape
 */
export function odataToInputFieldPropertyState(data: ODataInputFieldPropertyModel): InputFieldPropertyState {
    return {
        screenName: data.ScreenName,
        fieldName: data.FieldName,
        fieldValue: data.FieldValue,
        screenPos: parseInt(data.ScreenPos, 10),
        fieldPos: parseInt(data.FieldPos, 10),
        scannable: typeof data.Scannable === 'string' ? data.Scannable === 'X' : data.Scannable,
        mandatory: typeof data.Mandatory === 'string' ? data.Mandatory === 'X' : data.Mandatory,
        readonly: typeof data.ReadOnly === 'string' ? data.ReadOnly === 'X' : data.ReadOnly,
        minLength: parseInt(data.MinLength, 10),
        maxLength: parseInt(data.MaxLength, 10),
        minValue: parseInt(data.MinValue, 10),
        maxValue: parseInt(data.MaxValue, 10),
        pattern: data.Pattern,
        patternScan: data.PatternScan,
        valueHelperID: data.ValueHelperID,
        process: data.Process
            ? <ConfigConstants.PROCESS_INDICATOR>data.Process
            : ConfigConstants.PROCESS_INDICATOR.INVIVO,
        defaultValue: data.DefaultValue,
    };
}

/** Convert an {@link InputFieldPropertyModel} to the serializable store shape. */
export function inputFieldPropertyModelToState(model: InputFieldPropertyModel): InputFieldPropertyState {
    return {
        screenName: model.screenName,
        fieldName: model.fieldName,
        fieldValue: model.fieldValue,
        screenPos: model.screenPos,
        fieldPos: model.fieldPos,
        scannable: model.scannable,
        mandatory: model.mandatory,
        readonly: model.readonly,
        minLength: model.minLength,
        maxLength: model.maxLength,
        minValue: model.minValue,
        maxValue: model.maxValue,
        pattern: model.pattern,
        patternScan: model.patternScan,
        valueHelperID: model.valueHelperID,
        process: model.process,
        defaultValue: model.defaultValue,
    };
}

/** Convert a serializable store entry back to an {@link InputFieldPropertyModel} instance. */
export function inputFieldPropertyStateToModel(state: InputFieldPropertyState): InputFieldPropertyModel {
    return new InputFieldPropertyModel(
        state.screenName,
        state.fieldName,
        state.fieldValue,
        state.screenPos,
        state.fieldPos,
        state.scannable,
        state.mandatory,
        state.readonly,
        state.minLength,
        state.maxLength,
        state.minValue,
        state.maxValue,
        state.pattern,
        state.patternScan,
        state.valueHelperID,
        state.process,
        state.defaultValue,
    );
}

/** Convert an array of serializable store entries back to {@link InputFieldPropertyModel} instances. */
export function inputFieldPropertiesToModels(states: InputFieldPropertyState[]): InputFieldPropertyModel[] {
    return states.map(inputFieldPropertyStateToModel);
}
